import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Avatar } from '../../components/Avatar.js';
import { EmptyState, Spinner } from '../../components/Feedback.js';
import { useChat, type ChatMessage } from '../../state/chat.js';
import { useMyId } from '../../state/session.js';
import { toast, useHideNav } from '../../state/ui.js';
import { ChatInfoSheet } from './ChatInfoSheet.js';
import { Composer } from './Composer.js';
import { MessageActionsSheet } from './MessageActionsSheet.js';
import { MessageBubble } from './MessageBubble.js';
import { VerlaufsAntraege, VerlaufsGrenze, useVerlaufsantraege } from './VerlaufBand.js';
import { praesenzVon } from './praesenz.js';
import { chatOffenSetzen, untenSetzen } from '../../state/sichtbarkeit.js';
import {
  conversationAvatar,
  conversationTitle,
  counterpartOf,
  dayKey,
  formatDayLabel,
  formatLastSeen,
  isGrouped,
  memberCountLabel,
  memberName,
  typingLabel,
} from './helpers.js';

const NO_MESSAGES: ChatMessage[] = [];
const NO_TYPING: { userId: string; until: number }[] = [];

type RenderItem =
  | { kind: 'day'; key: string; label: string }
  | { kind: 'unread'; key: string }
  | {
      kind: 'message';
      key: string;
      message: ChatMessage;
      showSender: boolean;
      showAvatar: boolean;
    };

/** Keeps the composer above the on-screen keyboard on iOS (100dvh stays put). */
function useKeyboardInset(): number {
  const [inset, setInset] = useState(0);
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return undefined;
    const update = () => {
      const overlap = window.innerHeight - viewport.height - viewport.offsetTop;
      setInset(overlap > 60 ? Math.round(overlap) : 0);
    };
    update();
    viewport.addEventListener('resize', update);
    viewport.addEventListener('scroll', update);
    return () => {
      viewport.removeEventListener('resize', update);
      viewport.removeEventListener('scroll', update);
    };
  }, []);
  return inset;
}

export function ChatScreen() {
  useHideNav();
  const navigate = useNavigate();
  const myId = useMyId();
  const params = useParams();
  const conversationId = params.conversationId ?? '';
  // Welcher Chat gerade offen ist – für laufende asynchrone Arbeit, die den
  // Wechsel überleben würde (siehe `jumpTo`).
  const aktuellerChat = useRef(conversationId);
  aktuellerChat.current = conversationId;
  const keyboardInset = useKeyboardInset();
  useVerlaufsantraege(conversationId);

  const conversation = useChat(
    (state) => state.conversations.find((item) => item.id === conversationId) ?? null,
  );
  const messages = useChat((state) => state.messages[conversationId] ?? NO_MESSAGES);
  const loading = useChat((state) => state.loading[conversationId] ?? false);
  const hasMore = useChat((state) => state.hasMore[conversationId] ?? false);
  const typingEntries = useChat((state) => state.typing[conversationId] ?? NO_TYPING);
  /*
   * Nur der Eintrag des Gegenübers, nicht die ganze Karte: Sonst zeichnete
   * jede Online-Meldung irgendeines Kontakts diesen Chat neu (siehe
   * `praesenz.ts`). In einer Gruppe gibt es kein Gegenüber – dann gar nichts.
   */
  const gegenueberId = conversation ? (counterpartOf(conversation, myId)?.userId ?? null) : null;
  const gegenueberPraesenz = useChat((state) => praesenzVon(state.presence, gegenueberId));

  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const atBottom = useRef(true);
  const restore = useRef<{ height: number; top: number } | null>(null);
  const didInitialScroll = useRef(false);
  const unreadAnchor = useRef<{
    conversationId: string;
    enabled: boolean;
    lastReadId: string | null;
  }>({
    conversationId: '',
    enabled: false,
    lastReadId: null,
  });

  const [showJump, setShowJump] = useState(false);
  const [replyTo, setReplyTo] = useState<ChatMessage | null>(null);
  const [actionsFor, setActionsFor] = useState<ChatMessage | null>(null);
  const [infoOpen, setInfoOpen] = useState(false);

  // Dieser Chat liegt jetzt vor Augen: Der Server schickt dafür keine
  // Mitteilungen, und die Liste zählt hier nichts hoch (`state/sichtbarkeit.ts`).
  useEffect(() => {
    if (!conversationId) return undefined;
    chatOffenSetzen(conversationId);
    return () => chatOffenSetzen(null);
  }, [conversationId]);

  useEffect(() => {
    if (!conversationId) return;
    didInitialScroll.current = false;
    atBottom.current = true;
    restore.current = null;
    setShowJump(false);
    setReplyTo(null);
    setActionsFor(null);
    setInfoOpen(false);
    void useChat.getState().ensureConversation(conversationId);
    useChat
      .getState()
      .loadMessages(conversationId)
      .catch(() => toast('Nachrichten konnten nicht geladen werden', 'error'));
  }, [conversationId]);

  // Freeze the read marker when the chat opens so the divider does not jump
  // away the moment the messages are marked as read.
  if (conversation && unreadAnchor.current.conversationId !== conversationId) {
    const me = conversation.members.find((member) => member.userId === myId);
    unreadAnchor.current = {
      conversationId,
      enabled: conversation.unreadCount > 0,
      lastReadId: me?.lastReadMessageId ?? null,
    };
  }

  const items = useMemo<RenderItem[]>(() => {
    const out: RenderItem[] = [];
    const anchor = unreadAnchor.current;
    let previous: ChatMessage | undefined;
    let dividerPlaced = false;

    for (const message of messages) {
      let breakGroup = false;
      if (!previous || dayKey(previous.createdAt) !== dayKey(message.createdAt)) {
        out.push({
          kind: 'day',
          key: `day-${dayKey(message.createdAt)}`,
          label: formatDayLabel(message.createdAt),
        });
        breakGroup = true;
      }
      const isTheirs = message.senderId != null && message.senderId !== myId;
      const afterRead = anchor.lastReadId == null || message.id > anchor.lastReadId;
      if (anchor.enabled && !dividerPlaced && isTheirs && afterRead) {
        out.push({ kind: 'unread', key: 'unread-divider' });
        dividerPlaced = true;
        breakGroup = true;
      }
      const grouped = !breakGroup && isGrouped(previous, message);
      out.push({
        kind: 'message',
        key: message.id,
        message,
        showSender: !grouped,
        showAvatar: !grouped,
      });
      previous = message;
    }
    return out;
  }, [messages, myId, conversationId, conversation]);

  useLayoutEffect(() => {
    const element = scrollRef.current;
    if (!element) return;
    if (restore.current) {
      const { height, top } = restore.current;
      restore.current = null;
      element.scrollTop = element.scrollHeight - height + top;
      return;
    }
    if (!didInitialScroll.current) {
      if (messages.length === 0) return;
      didInitialScroll.current = true;
      element.scrollTop = element.scrollHeight;
      return;
    }
    if (atBottom.current) element.scrollTop = element.scrollHeight;
  }, [items, messages.length, loading]);

  // Late-loading media must not push the newest message out of sight.
  useEffect(() => {
    const element = scrollRef.current;
    const content = contentRef.current;
    if (!element || !content || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => {
      if (atBottom.current) element.scrollTop = element.scrollHeight;
    });
    observer.observe(content);
    return () => observer.disconnect();
  }, [conversationId]);

  useEffect(() => {
    if (messages.length > 0 && atBottom.current) useChat.getState().markRead(conversationId);
  }, [messages, conversationId]);

  function onScroll() {
    const element = scrollRef.current;
    if (!element) return;
    const distance = element.scrollHeight - element.scrollTop - element.clientHeight;
    const bottom = distance < 80;
    if (bottom !== atBottom.current) {
      atBottom.current = bottom;
      untenSetzen(bottom);
      setShowJump(!bottom);
      if (bottom) useChat.getState().markRead(conversationId);
    }
    if (element.scrollTop < 260 && hasMore && !loading && !restore.current) {
      restore.current = { height: element.scrollHeight, top: element.scrollTop };
      void useChat.getState().loadOlder(conversationId);
    }
  }

  function scrollToBottom(smooth = false) {
    const element = scrollRef.current;
    if (!element) return;
    atBottom.current = true;
    setShowJump(false);
    element.scrollTo({ top: element.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
  }

  /**
   * Zur zitierten Nachricht springen – und sie notfalls erst holen.
   *
   * Vorher wurde nur im gerenderten Verlauf gesucht. Ein Zitat zeigt aber
   * gerade auf etwas Älteres, und älter heisst in einem langen Chat: noch
   * nicht geladen. Der Tipp auf das Zitatfeld endete deshalb regelmässig bei
   * „Die Nachricht ist noch nicht geladen“ – während der Knopf „Ältere laden“
   * einen Zentimeter weiter oben genau das getan hätte.
   *
   * Die Kennungen sind zeitlich sortiert (UUIDv7), also lässt sich am
   * Vergleich mit der ältesten geladenen ablesen, ob weiteres Nachladen
   * überhaupt helfen kann. Die Obergrenze steht gegen den Fall, dass der
   * Server eine Nachricht nicht mehr liefert (gelöscht, kein Zugriff): Sonst
   * lädt die Schleife den ganzen Chat und findet trotzdem nichts.
   */
  /*
   * Mit `useCallback` und ohne Abhängigkeiten: Die Funktion geht an JEDE Blase
   * (`onJumpTo`), und eine neue Funktion je Zeichnen hebelte deren `memo` aus.
   * Sie liest alles Veränderliche über Refs und `getState()` – den Chat
   * eingeschlossen, siehe `zielChat`.
   */
  const jumpTo = useCallback(async (messageId: string) => {
    // Der Chat, für den dieser Sprung gilt. Wechselt der Anwender währenddessen
    // den Chat, hört die Schleife auf – sonst lüde sie Seite um Seite eines
    // Verlaufs nach, den niemand mehr ansieht.
    const zielChat = aktuellerChat.current;
    const selector =
      typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
        ? `[data-message-id="${CSS.escape(messageId)}"]`
        : `[data-message-id="${messageId}"]`;

    const suchen = () => scrollRef.current?.querySelector(selector) ?? null;
    const liste = () => useChat.getState().messages[zielChat] ?? [];
    const geladen = () => liste().some((item) => item.id === messageId);
    const aelteste = () => liste().find((item) => !item.pending)?.id ?? null;
    const bild = () => new Promise<void>((weiter) => requestAnimationFrame(() => weiter()));
    const warten = (ms: number) => new Promise<void>((weiter) => window.setTimeout(weiter, ms));

    let grund: 'gefunden' | 'nicht-im-verlauf' | 'aufgegeben' = 'aufgegeben';
    let ladeversuche = 0;
    let wartezeit = 0;

    while (ladeversuche < 20) {
      if (suchen() || geladen()) {
        grund = 'gefunden';
        break;
      }
      if (aktuellerChat.current !== zielChat) return;

      const zustand = useChat.getState();

      /*
       * Die Kennungen sind zeitlich sortiert (UUIDv7).
       *
       * Liegt das Ziel NICHT vor der ältesten geladenen Nachricht, kann
       * Nachladen es nicht herbeischaffen – es müsste dann schon in der Liste
       * stehen. Ohne diesen Vergleich lief die Schleife zwanzig Seiten weit,
       * also über tausend Nachrichten, für ein Ziel, das dort nie auftauchen
       * kann. (Dieser Vergleich stand vorher nur im Kommentar.)
       */
      const rand = aelteste();
      if (rand && messageId >= rand) {
        grund = 'nicht-im-verlauf';
        break;
      }
      if (!(zustand.hasMore[zielChat] ?? false)) {
        grund = 'nicht-im-verlauf';
        break;
      }

      /*
       * Läuft schon ein Ladevorgang – das Scrollen stösst denselben an –,
       * kehrt `loadOlder` sofort und wirkungslos zurück. Dann wird gewartet,
       * ohne einen Versuch zu verbrauchen: höchstens fünf Sekunden, damit ein
       * hängender Server die Schleife nicht festhält.
       */
      if (zustand.loading[zielChat]) {
        if (wartezeit >= 5000) break;
        wartezeit += 100;
        await warten(100);
        continue;
      }

      const vorher = liste().length;
      ladeversuche += 1;
      await zustand.loadOlder(zielChat);
      // Nichts dazugekommen und auch nichts unterwegs: Der Aufruf ist
      // gescheitert (`loadOlder` verschluckt seinen Fehler). Weitere Versuche
      // würden nur dasselbe tun.
      if (liste().length === vorher && !useChat.getState().loading[zielChat]) break;
    }

    if (aktuellerChat.current !== zielChat) return;
    // Nach dem Nachladen braucht React ein Bild, bevor die Zeile im DOM steht.
    if (!suchen()) await bild();

    const target = suchen();
    if (!target) {
      /*
       * Drei Ausgänge, drei Auskünfte.
       *
       * Vorher hiess jeder Fehlschlag „Die Nachricht ist nicht mehr da“ – auch
       * dann, wenn nur das Netz weg war. Das ist die schlechteste der drei
       * Auskünfte: Sie klingt endgültig und stimmt genau dann nicht, wenn ein
       * zweiter Versuch geholfen hätte.
       */
      toast(
        grund === 'nicht-im-verlauf'
          ? 'Die Nachricht ist nicht mehr da'
          : 'Der Verlauf liess sich gerade nicht weit genug laden. Versuch es noch einmal.',
        'info',
      );
      return;
    }
    target.scrollIntoView({ block: 'center', behavior: 'smooth' });
    target.classList.add('msg-row-flash');
    window.setTimeout(() => target.classList.remove('msg-row-flash'), 1400);
  }, []);

  /** Ob hinter meiner Grenze wirklich etwas liegt – ändert zwei Texte. */
  const hatGrenze = conversation?.verdeckterVerlauf ?? false;

  const avatar = conversation ? conversationAvatar(conversation, myId) : null;
  const counterpart = conversation ? counterpartOf(conversation, myId) : null;
  const online = counterpart ? (gegenueberPraesenz?.online ?? false) : false;
  const typingNames = typingEntries
    .filter((entry) => entry.userId !== myId)
    .map((entry) => {
      const member = conversation?.members.find((item) => item.userId === entry.userId);
      return member ? memberName(member) : 'Jemand';
    });

  let status = 'Chat wird geladen …';
  if (typingNames.length > 0) {
    status = typingLabel(typingNames);
  } else if (conversation?.type === 'group') {
    status = memberCountLabel(conversation);
  } else if (counterpart) {
    status = online
      ? 'online'
      : formatLastSeen(gegenueberPraesenz?.lastSeenAt ?? counterpart.user.lastSeenAt);
  }

  return (
    <div className="app-main msg-screen" style={{ paddingBottom: keyboardInset }}>
      <header className="app-header msg-header">
        <button
          type="button"
          className="icon-btn"
          aria-label="Zurück zu den Chats"
          onClick={() => navigate('/chats')}
        >
          ‹
        </button>
        <button
          type="button"
          className="msg-header-main"
          onClick={() => setInfoOpen(true)}
          aria-label="Chat-Info öffnen"
        >
          {avatar ? (
            <Avatar
              name={avatar.name}
              id={avatar.id}
              url={avatar.url}
              size={38}
              online={conversation?.type === 'direct' ? online : undefined}
            />
          ) : (
            <span className="skeleton" style={{ width: 38, height: 38, borderRadius: '50%' }} />
          )}
          <span style={{ minWidth: 0 }}>
            <span className="msg-header-title truncate">
              {conversation ? conversationTitle(conversation, myId) : 'Chat'}
            </span>
            <span
              className={`msg-header-status truncate ${typingNames.length > 0 ? 'is-typing' : ''}`}
            >
              {status}
            </span>
          </span>
        </button>
        <button
          type="button"
          className="icon-btn"
          aria-label="Chat-Info"
          onClick={() => setInfoOpen(true)}
        >
          ⋮
        </button>
      </header>

      <VerlaufsAntraege conversation={conversation} myId={myId} />

      <div className="msg-body">
        <div className="msg-scroll" ref={scrollRef} onScroll={onScroll}>
          <div className="msg-content" ref={contentRef}>
            {hasMore && (
              <div className="msg-loader">
                {loading ? <Spinner label="Ältere Nachrichten" /> : '…'}
              </div>
            )}

            {/*
             * Unter dem Nachlader: Erst wenn nichts Älteres mehr kommt, steht
             * hier wirklich der Anfang dessen, was ich sehen darf. Darüber
             * wäre der Satz „hier beginnt, was du sehen kannst" schlicht
             * falsch, solange noch eine Seite unterwegs ist.
             */}
            {!hasMore && <VerlaufsGrenze conversation={conversation} myId={myId} />}

            {messages.length === 0 && loading && (
              <div className="msg-loader">
                <Spinner label="Nachrichten werden geladen" />
              </div>
            )}

            {/*
             * Zwei leere Verläufe, zwei Wahrheiten. Wer gerade erst
             * dazugekommen ist, sieht womöglich nur deshalb nichts, weil
             * alles vor seinem Beitritt liegt – „schreib die erste
             * Nachricht" wäre dort schlicht falsch.
             */}
            {messages.length === 0 &&
              !loading &&
              (hatGrenze ? (
                <EmptyState
                  emoji="🕐"
                  title="Seit deinem Beitritt ist hier nichts geschrieben worden"
                  description="Was davor liegt, siehst du nur, wenn alle anderen zustimmen."
                />
              ) : (
                <EmptyState
                  emoji="👋"
                  title="Noch keine Nachrichten"
                  description="Schreib die erste Nachricht – sie landet sofort bei allen Mitgliedern."
                />
              ))}

            {items.map((item) => {
              if (item.kind === 'day') {
                return (
                  <div key={item.key} className="msg-day">
                    <span>{item.label}</span>
                  </div>
                );
              }
              if (item.kind === 'unread') {
                return (
                  <div key={item.key} className="msg-unread">
                    <span>Neue Nachrichten</span>
                  </div>
                );
              }
              return (
                <MessageBubble
                  key={item.key}
                  message={item.message}
                  conversation={conversation}
                  showSender={item.showSender}
                  showAvatar={item.showAvatar}
                  onOpenActions={setActionsFor}
                  onJumpTo={jumpTo}
                />
              );
            })}
          </div>
        </div>

        {showJump && (
          <button
            type="button"
            className="msg-jump"
            aria-label="Zu den neuesten Nachrichten"
            onClick={() => scrollToBottom(true)}
          >
            ↓
            {conversation && conversation.unreadCount > 0 && (
              <span className="chat-badge">{conversation.unreadCount}</span>
            )}
          </button>
        )}
      </div>

      <Composer
        conversationId={conversationId}
        conversation={conversation}
        replyTo={replyTo}
        onCancelReply={() => setReplyTo(null)}
        onScrollToBottom={() => scrollToBottom()}
      />

      <MessageActionsSheet
        message={actionsFor}
        conversation={conversation}
        onClose={() => setActionsFor(null)}
        onReply={setReplyTo}
      />

      <ChatInfoSheet
        open={infoOpen}
        conversation={conversation}
        onClose={() => setInfoOpen(false)}
      />
    </div>
  );
}
